import React, { useState, Component } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { blogPosts } from '../data/blogData';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Clock, Share2, Facebook, Twitter, Mail, Link as LinkIcon, Check } from 'lucide-react';
export function BlogDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const post = blogPosts.find(p => p.id === id);
  const [copied, setCopied] = useState(false);
  if (!post) {
    return <Navigate to="/blog" replace data-id="element-302" />;
  }
  const relatedPosts = blogPosts.filter(p => p.category === post.category && p.id !== post.id).slice(0, 3);
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post.title;
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${url}`)}`;
        break;
    }
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <Layout data-id="element-303">
      <article data-id="element-304">
        {/* Hero Section */}
        <div className="relative h-[60vh] min-h-[500px] flex items-end pb-16" data-id="element-305">
          <div className="absolute inset-0 z-0" data-id="element-306">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-307" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" data-id="element-308" />
          </div>

          <div className="container-custom relative z-10" data-id="element-309">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6" data-id="element-310">
              <ArrowLeft className="w-4 h-4" data-id="element-311" />
              Back to Blog
            </Link>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-312">
              <span className="inline-block px-3 py-1 bg-[#0A1F44] text-white text-xs font-semibold uppercase tracking-wider rounded-full mb-4" data-id="element-313">
                {post.category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 max-w-4xl" data-id="element-314">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm" data-id="element-315">
                <span className="flex items-center gap-2" data-id="element-316">
                  <User className="w-4 h-4" data-id="element-317" />
                  {post.author}
                </span>
                <span className="flex items-center gap-2" data-id="element-318">
                  <Calendar className="w-4 h-4" data-id="element-319" />
                  {post.date}
                </span>
                <span className="flex items-center gap-2" data-id="element-320">
                  <Clock className="w-4 h-4" data-id="element-321" />
                  {post.readTime}
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container-custom py-16" data-id="element-322">
          <div className="max-w-3xl mx-auto relative" data-id="element-323">
            {/* Share Floating Bar */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="hidden lg:flex flex-col gap-4 absolute -left-20 top-0 sticky top-32" data-id="element-324">
              <span className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-2" style={{
              writingMode: 'vertical-rl'
            }} data-id="element-325">
                Share
              </span>
              <button onClick={() => handleShare('facebook')} className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on Facebook" data-id="element-326">
                <Facebook className="w-5 h-5" data-id="element-327" />
              </button>
              <button onClick={() => handleShare('twitter')} className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on Twitter" data-id="element-328">
                <Twitter className="w-5 h-5" data-id="element-329" />
              </button>
              <button onClick={() => handleShare('email')} className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share via Email" data-id="element-330">
                <Mail className="w-5 h-5" data-id="element-331" />
              </button>
              <button onClick={handleCopyLink} className="w-10 h-10 rounded-full bg-[#0A1F44] text-white flex items-center justify-center hover:opacity-80 transition-opacity relative" aria-label="Copy Link" data-id="element-332">
                {copied ? <Check className="w-5 h-5" data-id="element-333" /> : <LinkIcon className="w-5 h-5" data-id="element-334" />}
              </button>
              {copied && <span className="text-sm text-[#0A1F44] font-medium absolute left-12 whitespace-nowrap" data-id="element-335">
                  Link copied!
                </span>}
            </motion.div>

            {/* Article Body */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="prose prose-lg max-w-none prose-headings:font-serif prose-a:text-[#C8102E]" dangerouslySetInnerHTML={{
            __html: post.content
          }} style={{
            color: '#4B5563'
          }} data-id="element-336" />

            {/* Author Info */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="mt-16 p-8 bg-[#F5F7FA] rounded-xl border-l-4 border-[#C8102E]" data-id="element-337">
              <div className="flex items-start gap-4" data-id="element-338">
                <div className="w-16 h-16 bg-[#0A1F44] rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0" data-id="element-339">
                  {post.author.charAt(0)}
                </div>
                <div data-id="element-340">
                  <h3 className="text-xl font-serif font-bold text-[#0A1F44] mb-2" data-id="element-341">
                    About {post.author}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-342">
                    {post.author} serves as a pastor at The Household of Christ
                    Church, dedicated to teaching God's Word and helping people
                    grow in their faith journey.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && <section className="py-16 bg-gray-50 border-t" data-id="element-343">
          <div className="container-custom" data-id="element-344">
            <div className="max-w-5xl mx-auto" data-id="element-345">
              <div className="flex items-center justify-between mb-8" data-id="element-346">
                <h2 className="text-2xl font-serif font-bold text-[#0A1F44]" data-id="element-347">
                  Related Articles
                </h2>
                <Link to="/blog" className="text-[#C8102E] font-medium hover:underline" data-id="element-348">
                  View All
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-349">
                {relatedPosts.map((relatedPost, index) => <motion.article key={relatedPost.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group" data-id="element-350">
                    <Link to={`/blog/${relatedPost.id}`} className="block" data-id="element-351">
                      <div className="relative h-40 overflow-hidden" data-id="element-352">
                        <img src={relatedPost.image} alt={relatedPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-353" />
                      </div>
                      <div className="p-5" data-id="element-354">
                        <span className="text-[#0A1F44] text-xs font-semibold uppercase tracking-wider mb-2 block" data-id="element-355">
                          {relatedPost.category}
                        </span>
                        <h3 className="font-serif font-bold text-[#0A1F44] mb-2 line-clamp-2 group-hover:text-[#C8102E] transition-colors" data-id="element-356">
                          {relatedPost.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-357">
                          <span className="flex items-center gap-1" data-id="element-358">
                            <User className="w-3 h-3" data-id="element-359" />
                            {relatedPost.author}
                          </span>
                          <span className="flex items-center gap-1" data-id="element-360">
                            <Clock className="w-3 h-3" data-id="element-361" />
                            {relatedPost.readTime}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>)}
              </div>
            </div>
          </div>
        </section>}

      {/* Newsletter CTA */}
      <section className="py-16 bg-[#0A1F44]" data-id="element-362">
        <div className="container-custom text-center" data-id="element-363">
          <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-364">
            Stay Connected
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-365">
            Subscribe to our newsletter for weekly devotionals, church updates,
            and encouragement delivered straight to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-366">
            <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C8102E]" data-id="element-367" />
            <Button className="bg-[#C8102E] hover:bg-[#A00D24] text-white border-none" data-id="element-368">
              Subscribe
            </Button>
          </form>
        </div>
      </section>

      {/* Custom Styles for Article Content */}
      <style data-id="element-369">{`
        .prose h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.875rem;
          font-weight: 700;
          color: #0A1F44;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.3;
        }

        .prose p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
        }

        .prose blockquote {
          border-left-color: #C8102E;
          background-color: #F5F7FA;
          padding: 1.5rem;
          border-radius: 0 0.5rem 0.5rem 0;
          font-style: italic;
          color: #0A1F44;
          margin: 2rem 0;
        }

        .prose ul {
          margin-top: 1rem;
          margin-bottom: 1.5rem;
        }

        .prose li {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .prose strong {
          color: #0A1F44;
        }
      `}</style>
    </Layout>;
}